import {
    Card,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { useAppSelector } from "@/hooks/types";
import {
    getIsAtsScanEnabled,
    getIsUserJobsEnabled,
} from "@/store/tenant/selectors";
import { Resume, User, IUserJob } from "@/types";
import { CreditActionCode } from "@/types/creditAction";
import { ICreditTransaction } from "@/types/creditTransaction";

interface QuickStatsData {
    users: User[];
    transactions: ICreditTransaction[];
    resumes: Resume[];
    userJobs: IUserJob[];
}

interface QuickStatsCardsProps {
    data?: QuickStatsData;
}

const StatCard = ({ title, value }: { title: string; value: number }) => {
    return (
        <Card>
            <CardHeader className="relative p-2 text-center">
                <CardDescription className="font-medium">
                    {title}
                </CardDescription>
                <CardTitle className="text-3xl font-semibold tabular-nums">
                    {value ? value.toLocaleString() : 0}
                </CardTitle>
            </CardHeader>
        </Card>
    );
};

export const QuickStatsCards = ({ data }: QuickStatsCardsProps) => {
    const {
        users = [],
        transactions = [],
        resumes = [],
        userJobs = [],
    } = data ?? {};

    const isUserJobsEnabled = useAppSelector(getIsUserJobsEnabled);
    const isAtsScanEnabled = useAppSelector(getIsAtsScanEnabled);

    const downloads = transactions?.filter(
        (transaction: ICreditTransaction) =>
            transaction.actionCode === CreditActionCode.DOWNLOAD_DOCUMENT,
    );

    const atsScan = transactions?.filter(
        (transaction: ICreditTransaction) =>
            transaction.actionCode === CreditActionCode.ATS_SCAN,
    );

    if (!data) return null;

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-7">
            <StatCard
                title="New Users"
                value={users?.length}
            />

            <StatCard
                title="New Resumes"
                value={resumes?.length}
            />

            <StatCard
                title="Downloads"
                value={downloads?.length}
            />

            {isUserJobsEnabled && (
                <StatCard
                    title="User Jobs"
                    value={userJobs?.length}
                />
            )}

            {isAtsScanEnabled && (
                <StatCard
                    title="ATS Scans"
                    value={atsScan?.length}
                />
            )}
        </div>
    );
};
